import { DataGrid, DataGridColumn, DataGridRef } from '@cimplico/components';
import { Ref, useState, useEffect } from 'react';
import { useOtherSchedules } from '../../queries/use-other-schedules';
import OtherScheduleRow from './OtherScheduleRow';
import { ROW_SIZES } from '../../constants';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import EmptyStateNew from '../../components/empty-state/EmptyStateNew';
import NoResults from '../../components/empty-state/NoResults';
import { RectangleGroupIcon } from '@heroicons/react/24/outline';
import { GenericWorksheet } from '../../types/worksheet';

const columns: DataGridColumn[] = [
    { header: 'Schedule', width: '2fr' },
    { header: 'Last Updated' },
    { header: 'Entries', alignment: 'center' },
    { header: 'Files', alignment: 'center' },
    { header: 'Outstanding', alignment: 'center' },
    { header: 'Assigned' },
];

type OtherSchedulesTableProps = {
    filterQuery?: string;
    sortQuery?: string;
    searchQuery?: string;
    tab?: string;
    onUploading: (uploading: boolean) => void;
    onSelect: (ids: string[]) => void;
    tableRef: Ref<DataGridRef> | null;
    onData?: (rows: GenericWorksheet[]) => void;
};

function OtherSchedulesTable({
    tableRef,
    onSelect,
    filterQuery,
    sortQuery,
    searchQuery,
    onUploading,
    tab,
    onData,
}: OtherSchedulesTableProps): JSX.Element {
    const [currentFileDrawer, setCurrentFileDrawer] = useState<string>();
    const [limit, setLimit] = useState(ROW_SIZES[0]);
    const [page, setPage] = useState(1);
    const { readOnly, id } = useWorkpaperContext();
    const otherSchedulesQuery = useOtherSchedules({ limit, page, filterQuery, sortQuery, searchQuery, tab });

    useEffect(() => {
        if (otherSchedulesQuery.isSuccess) {
            onData?.(otherSchedulesQuery.data.otherSchedules);
        }
    }, [otherSchedulesQuery.isSuccess, otherSchedulesQuery.data, onData]);

    if (otherSchedulesQuery.isLoading) {
        return <></>;
    }

    if (!otherSchedulesQuery.isSuccess) {
        return <p>Error</p>;
    }

    function selectSchedules(ids: string[]): void {
        onSelect(ids);
    }

    const { otherSchedules, totalItems } = otherSchedulesQuery.data;
    const showEmpty = !searchQuery;
    return (
        <div>
            <DataGrid
                columns={columns}
                rowSizes={ROW_SIZES}
                totalRows={totalItems}
                onSelect={selectSchedules}
                ref={tableRef}
                onPageChange={setPage}
                onRowSizeChange={setLimit}
                emptyMessage={
                    showEmpty ? (
                        <EmptyStateNew
                            icon={<RectangleGroupIcon />}
                            actionText="New Schedule"
                            helpLink="https://learn.cimplico.com/docs/other-schedules-overview"
                            helpText="Prepare a worksheet for a non-trial balance item."
                            title="Add your first Other Schedule"
                            navigateTo={`/workpapers/${id}/other-schedules/new`}
                            disableActionButton={readOnly}
                        />
                    ) : (
                        <NoResults search={searchQuery} />
                    )
                }
            >
                {otherSchedules.map((os) => {
                    return (
                        <OtherScheduleRow
                            id={os.id}
                            key={os.id}
                            otherSchedule={os}
                            onUploading={onUploading}
                            disabled={readOnly}
                            onFileDrawerOpen={setCurrentFileDrawer}
                            currentFileDrawer={currentFileDrawer}
                        />
                    );
                })}
            </DataGrid>
        </div>
    );
}
export default OtherSchedulesTable;
